import type { PaginationResultDto } from '@bondsports/types';
import type { QueryKey } from '@tanstack/react-query';
import * as React from 'react';
import {
	useDebouncedInfiniteQuery,
	type IDebouncedInfiniteQueryPageInput,
} from '../../../hooks/use-debounced-infinite-query';

export type IListPageDataPageInput = IDebouncedInfiniteQueryPageInput;

/**
 * Standard contract for any data hook that powers a `<ListPage>`.
 * The `useData` escape-hatch on `<ListPage>` accepts any hook that returns this shape.
 */
export interface IListPageDataResult<TRow> {
	items: TRow[];
	isLoading: boolean;
	isFetching: boolean;
	error: Error | null;
	hasMore: boolean;
	loadMore: () => void;
	refetch: () => void;
}

/**
 * Live input forwarded to a hook-backed `useData` implementation by `<ListPage>` on every
 * render. `search` is already debounced according to `search.debounceMs` (defaults to 250ms),
 * and `filters` has had any `type: 'date-range'` selections resolved into the
 * `IListPageDateRange` payload returned by `option.range()`. Consumer hooks use these as
 * dependencies of their own React-Query cache key (or the equivalent in their data layer)
 * so the list reacts to search and filter changes the same way the API-backed path does.
 */
export interface IListPageDataHookInput {
	search: string;
	filters: Record<string, unknown>;
}

export type ListPageDataHook<TRow> = (input: IListPageDataHookInput) => IListPageDataResult<TRow>;

export interface IUseListPageDataInput<TItem, TRow = TItem> {
	/**
	 * Fetches a single page of items. Receives the merged page+search+filter input and an optional
	 * `AbortSignal` so the consumer can wire it into the underlying HTTP client when supported.
	 */
	apiMethod: (input: IListPageDataPageInput, options?: { signal?: AbortSignal }) => Promise<PaginationResultDto<TItem>>;
	/**
	 * Optional transform from the API response to the row type the list renders.
	 * Defaults to identity (`response.data as unknown as TRow[]`).
	 */
	apiResponseParser?: (response: PaginationResultDto<TItem>) => TRow[];
	/**
	 * React Query key. Search and applied filters are appended automatically.
	 */
	queryKey: QueryKey;
	/** Current search input (consumer-controlled). Debounced internally before triggering a fetch. */
	search?: string;
	/** Currently applied filter values. Whatever shape the consumer wants; merged into the query key. */
	filters?: Record<string, unknown>;
	itemsPerPage?: number;
	enabled?: boolean;
	staleTime?: number;
	cacheTime?: number;
	searchDebounceMs?: number;
	onError?: (error: unknown) => void;
}

/**
 * The bundled data hook powering the high-level `<ListPage>` component. Curates the shared
 * `useDebouncedInfiniteQuery` core down to the `IListPageDataResult` contract.
 */
export function useListPageData<TItem, TRow = TItem>({
	apiMethod,
	apiResponseParser,
	queryKey,
	search = '',
	filters,
	itemsPerPage,
	enabled,
	staleTime,
	cacheTime,
	searchDebounceMs,
	onError,
}: IUseListPageDataInput<TItem, TRow>): IListPageDataResult<TRow> {
	const { data, isLoading, isFetching, error, hasNextPage, fetchNextPage, refetch } =
		useDebouncedInfiniteQuery<TItem>({
			queryKey,
			apiMethod,
			search,
			filters,
			itemsPerPage,
			enabled,
			staleTime,
			cacheTime,
			searchDebounceMs,
			onError,
		});

	const items = React.useMemo<TRow[]>(() => {
		const pages = data?.pages ?? [];
		if (apiResponseParser) {
			return pages.flatMap(page => apiResponseParser(page));
		}
		return pages.flatMap(page => (page?.data ?? []) as unknown as TRow[]);
	}, [data, apiResponseParser]);

	const loadMore = React.useCallback(() => {
		if (hasNextPage && !isFetching) {
			void fetchNextPage();
		}
	}, [hasNextPage, isFetching, fetchNextPage]);

	const wrappedRefetch = React.useCallback(() => {
		void refetch();
	}, [refetch]);

	return {
		items,
		isLoading,
		isFetching,
		error: (error as Error | null) ?? null,
		hasMore: Boolean(hasNextPage),
		loadMore,
		refetch: wrappedRefetch,
	};
}
